import { Outlet, Link, useNavigate, useLocation } from "react-router";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { CommandPalette } from "./CommandPalette";
import { KeyboardShortcutsModal } from "./KeyboardShortcutsModal";
import { HowItWorksModal } from "./HowItWorksModal";
import { useState, useEffect, useCallback, useRef } from "react";
import { Mail, X } from "lucide-react";
import { useCurrentUser, useResendVerificationEmail } from "../../hooks/useAPI";
import { useFirstTraceNotification } from "../../hooks/useFirstTraceNotification";
import { ChecklistWidget } from "./onboarding/ChecklistWidget";
import { toast, Toaster } from "sonner";
import { useAnalyticsPageView } from "../../hooks/useAnalytics";
import { OfflineBanner } from "./OfflineBanner";
import { ChainIntegrityBanner } from "./ChainIntegrityBanner";

// Linear-style "g + letter" quick-nav. Keep this map next to the shortcuts
// modal so both stay in sync — the modal reads human labels from SHORTCUTS.
// Exported for unit tests that assert parity with KeyboardShortcutsModal.
export const GOTO_ROUTES: Record<string, string> = {
  d: "/dashboard",             // g d → Dashboard / Overview
  o: "/dashboard",             // g o → Overview (alias)
  c: "/dashboard/o/cpi",       // g c → CPI (legacy)
  r: "/dashboard/review-queue",// g r → Review Queue
  a: "/dashboard/systems",     // g a → AI Systems
  t: "/dashboard/decision-log",// g t → Decision Log
  l: "/dashboard/decision-log",// g l → Decision Log (alias)
  p: "/dashboard/policy-engine", // g p → Policy Engine
  n: "/dashboard/anomalies",   // g n → Anomalies
  f: "/dashboard/o/feedback-analytics", // g f → Feedback (legacy)
  i: "/dashboard/connectors",  // g i → Konnektoren (Integration-Hub migriert)
  b: "/dashboard/billing",     // g b → Abrechnung
  s: "/dashboard/settings",    // g s → Einstellungen
};

const GOTO_WINDOW_MS = 1500;

export function DashboardLayout() {
  // Track page views on route changes (#50)
  useAnalyticsPageView();

  // SSE listener for the first-real-trace milestone (Phase 1 §3.6).
  // Surfaces a toast + invalidates the onboarding state cache so the
  // checklist crosses off trace_received without a manual refresh.
  useFirstTraceNotification();

  // useCurrentUser() must be called BEFORE any useEffect that references
  // `currentUser` — JavaScript temporal dead zone (TDZ): the destructured
  // const isn't initialized until its declaration line, but a useEffect
  // closure captures the binding by name. With currentUser declared
  // after the compactMode effect, the effect's first run threw
  // `ReferenceError: Cannot access 'currentUser' before initialization`.
  const { data: currentUser } = useCurrentUser();

  // Apply the user's Compact Mode preference globally. Reads
  // `currentUser.uiPreferences.compactMode` and toggles the
  // `data-compact="true"` attribute on <html>. Theme.css overrides the
  // density tokens (header-height, space-section, space-card-padding,
  // body line-height) when the attribute is present. Cleaned up on
  // unmount so landing/auth pages reset to the default scale.
  useEffect(() => {
    const compact = currentUser?.uiPreferences?.compactMode === true;
    const html = document.documentElement;
    if (compact) html.setAttribute("data-compact", "true");
    else html.removeAttribute("data-compact");
    return () => html.removeAttribute("data-compact");
  }, [currentUser?.uiPreferences?.compactMode]);

  // Lock html + body scroll while the dashboard is mounted. The layout
  // root is `flex h-screen overflow-hidden` and `<main>` owns the scroll
  // container, but a handful of pages (Settings, Founder Analytics,
  // Billing → Usage, Decision Mining, Feedback, Overview) were leaking
  // a tiny amount of body-level scroll — the whole shell, sidebar and
  // all, would shift up when the user wheel-scrolled past the bottom.
  // We could not pin the exact rogue child, so this defensive guard
  // makes body scroll impossible regardless of what any descendant
  // tries: html + body get hard-locked while DashboardLayout is alive
  // and restored on unmount so landing/auth pages keep their scroll.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      bodyMargin: body.style.margin,
    };
    html.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.overflow = "hidden";
    body.style.height = "100%";
    body.style.margin = "0";
    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
      body.style.margin = prev.bodyMargin;
    };
  }, []);

  const navigate = useNavigate();
  // Pathname keyed wrapper drives a single page-level fade on route
  // change. Without the key, React-Router reuses the same DOM nodes
  // and the animation never re-fires; with it, each navigation
  // unmounts/remounts the wrapper and triggers `.animate-page-in`.
  const location = useLocation();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [dismissedVerifyBanner, setDismissedVerifyBanner] = useState(false);
  // currentUser is now hoisted to the top of the component (above the
  // compactMode useEffect) — see the comment up there for why.
  const resendVerification = useResendVerificationEmail();
  const handleCommandPaletteClose = useCallback(() => setIsCommandPaletteOpen(false), []);
  const showVerifyBanner = !dismissedVerifyBanner && currentUser && currentUser.emailVerified === false;

  // "g" prefix state for two-key quick-nav. Refs avoid re-renders on every
  // keystroke, which matters because the handler is bound to window.
  const gotoPendingRef = useRef(false);
  const gotoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Command Palette keyboard shortcut (Cmd+K / Ctrl+K), ? modal, g-prefix nav
  useEffect(() => {
    const clearGoto = () => {
      gotoPendingRef.current = false;
      if (gotoTimeoutRef.current) {
        clearTimeout(gotoTimeoutRef.current);
        gotoTimeoutRef.current = null;
      }
    };

    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el || !el.tagName) return false;
      return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K / Ctrl+K for Command Palette
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        clearGoto();
        setIsCommandPaletteOpen(true);
        return;
      }

      // ? for Keyboard Shortcuts Modal
      if (e.key === "?" && !e.metaKey && !e.ctrlKey) {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        clearGoto();
        setShowKeyboardShortcuts(true);
        return;
      }

      // Escape cancels a pending g-prefix
      if (e.key === "Escape" && gotoPendingRef.current) {
        clearGoto();
        return;
      }

      // Skip quick-nav when typing or when modifiers are held.
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) {
        return;
      }

      // Second key of a g-prefix sequence
      if (gotoPendingRef.current) {
        const target = GOTO_ROUTES[e.key.toLowerCase()];
        clearGoto();
        if (target) {
          e.preventDefault();
          navigate(target);
        }
        return;
      }

      // First key — start g-prefix window
      if (e.key === "g") {
        gotoPendingRef.current = true;
        gotoTimeoutRef.current = setTimeout(() => {
          gotoPendingRef.current = false;
          gotoTimeoutRef.current = null;
        }, GOTO_WINDOW_MS);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (gotoTimeoutRef.current) clearTimeout(gotoTimeoutRef.current);
    };
  }, [navigate]);

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      {/* Command Palette */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={handleCommandPaletteClose}
      />

      {/* Sidebar */}
      <Sidebar 
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          onMobileMenuToggle={() => setIsMobileSidebarOpen(prev => !prev)}
          onSearchClick={() => setIsCommandPaletteOpen(true)}
          isSearchOpen={isCommandPaletteOpen}
          onKeyboardShortcutsClick={() => setShowKeyboardShortcuts(true)}
          onHowItWorksClick={() => setShowHowItWorks(true)}
        />

        {/* Chain Integrity Banner — Phase 1 Track G.6 per ADR-PE-10.
            Polls /api/audit-integrity/snapshot every 30s; fires red banner
            on chain-verification failure. Dismissible per-session. Backs the
            "court-admissible" marketing claim with continuous customer-
            visible verification. Renders nothing when chain is healthy. */}
        <ChainIntegrityBanner />

        {/* Email Verification Banner */}
        {showVerifyBanner && (
          <div className="bg-surface-1 border-b border-border px-4 py-2.5 flex items-center justify-between gap-3 flex-shrink-0">
            <div className="flex items-center gap-2 text-sm text-foreground">
              <Mail className="w-4 h-4 flex-shrink-0" />
              <span>
                Please verify your email address.{" "}
                <Link to="/verify-email" className="font-semibold underline hover:no-underline">
                  Check your inbox
                </Link>
                {" or "}
                <button
                  onClick={() => resendVerification.mutate(undefined, {
                    onSuccess: () => toast.success("Verification email sent"),
                    onError: () => toast.error("Failed to send verification email. Please try again."),
                  })}
                  disabled={resendVerification.isPending}
                  className="font-semibold underline hover:no-underline disabled:opacity-50"
                >
                  {resendVerification.isPending ? "Sending..." : "resend"}
                </button>
                .
              </span>
            </div>
            <button
              onClick={() => setDismissedVerifyBanner(true)}
              aria-label="Dismiss verification banner"
              className="text-muted-foreground hover:text-foreground flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <main className="flex-1 overflow-y-auto px-4 pt-4 pb-24 lg:px-6 lg:pt-6 lg:pb-6">
          {/* Page Content from Router - 4K Display Scaling Container.
              `key={location.pathname}` forces React to unmount/remount
              the inner wrapper on each navigation, re-firing the
              `.animate-page-in` keyframe so the new page does a single
              200ms fade — replaces the per-card flicker pattern. */}
          <div
            key={location.pathname}
            className="mx-auto adp-page-fade"
            style={{ width: 'min(100%, 3000px)' }}
          >
            <Outlet />
          </div>
        </main>

        {/* Persistent activation checklist — hides itself when 100% or dismissed */}
        <div className="fixed bottom-4 right-4 z-30 hidden md:block">
          <ChecklistWidget />
        </div>
      </div>

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        isOpen={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      {/* How It Works Modal */}
      <HowItWorksModal
        isOpen={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
      />

      {/* Sonner Toaster for all toast notifications */}
      {/* Offline Banner — shows when disconnected or syncing queued actions */}
      <OfflineBanner />

      <Toaster
        position="top-center"
        offset="56px"
        toastOptions={{
          style: {
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            // Theme-aware soft radius via editorial token (with safe `, 0` fallback).
            borderRadius: 'var(--radius-toast, 0)',
            color: 'var(--foreground)',
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            // Editorial gets a subtle elevation lift; safe `, none` fallback
            // keeps toasts shadowless if the token is unset.
            boxShadow: 'var(--shadow-toast, none)',
            padding: '12px 16px',
          },
          className: 'adp-toast',
        }}
        duration={4000}
      />
    </div>
  );
}